import React, { useEffect, useMemo, useState, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faShieldAlt, faDollarSign, faExclamationTriangle, faChevronDown, faChevronRight, faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { certDisplayLabel } from '../services/profileService'
import { eeapService, type EeapRequestType, type EeapSummary } from '../services/eeapService'
import { DEFAULT_EEAP_PREAPPROVED_CATEGORIES } from '../utils/eeapPreapprovedCategories'
import SignaturePad, { type SignaturePadHandle } from './SignaturePad'
import { SignaturePadLock, useSignatureIdentity } from './SignatureAuthGate'

interface EEAPModalProps {
  isOpen: boolean
  onClose: () => void
}

const DEFAULT_ALLOWANCE = 200
const DEFAULT_CAP = 350

function money(n: number | undefined | null) {
  return Number(n || 0).toFixed(0)
}

function formatUsd(n: number | undefined | null) {
  return `$${Number(n || 0).toFixed(2)}`
}

const EEAPModal: React.FC<EEAPModalProps> = ({ isOpen, onClose }) => {
  const [summary, setSummary] = useState<EeapSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [badCertsExpanded, setBadCertsExpanded] = useState(false)

  const [selectedType, setSelectedType] = useState<EeapRequestType>('greenHand')
  const [exceptionAcknowledged, setExceptionAcknowledged] = useState(false)
  const [category, setCategory] = useState('')
  const [preapprovedCategories, setPreapprovedCategories] = useState<string[]>([...DEFAULT_EEAP_PREAPPROVED_CATEGORIES])
  const [description, setDescription] = useState('')
  const [url, setUrl] = useState('')
  const [purchaseMyself, setPurchaseMyself] = useState(false)
  const [policyConsent, setPolicyConsent] = useState(false)
  const [cost, setCost] = useState('')
  const [signatureDate, setSignatureDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [signatureDrawn, setSignatureDrawn] = useState(false)
  const [submittedConfirm, setSubmittedConfirm] = useState(false)
  const signaturePadRef = useRef<SignaturePadHandle>(null)
  const { unlocked, requestUnlock, lock, authRef, confirmPasswordModal } = useSignatureIdentity()

  useEffect(() => {
    if (isOpen) return
    lock()
    signaturePadRef.current?.clear()
    setSignatureDrawn(false)
  }, [isOpen, lock])

  const allowance = summary?.annualAllowance ?? DEFAULT_ALLOWANCE
  const cap = summary?.outstandingCap ?? DEFAULT_CAP
  const remaining = summary?.remainingAllowance ?? 0
  const outstandingEeap = summary?.outstandingBalance ?? 0
  const outstandingAdvance = summary?.outstandingAdvance ?? 0
  const outstandingEeapAdvance = summary?.outstandingEeapAdvance ?? 0
  const totalOutstanding = summary?.outstandingTotal
    ?? (outstandingEeap + outstandingAdvance + outstandingEeapAdvance)
  const resetDateLabel = useMemo(() => {
    if (!summary?.windowResetsOn) return ''
    try {
      return new Date(summary.windowResetsOn).toLocaleDateString()
    } catch {
      return ''
    }
  }, [summary?.windowResetsOn])

  const costCentsLike = useMemo(() => {
    const n = Number(cost)
    if (!Number.isFinite(n) || n <= 0) return null
    return n
  }, [cost])

  const needsEstimatedCost = selectedType === 'equipment'
  const estimatedCost = needsEstimatedCost ? costCentsLike : null

  const employeePortion = useMemo(() => {
    if (estimatedCost == null) return 0
    return Math.max(0, estimatedCost - remaining)
  }, [estimatedCost, remaining])

  const projected = outstandingEeap + employeePortion
  const projectedUnderCap = projected <= cap
  const underCap = outstandingEeap < cap

  const tenureEligible = summary?.tenureEligible || { greenHand: true, boots: false, equipment: false }
  const tenureOk = tenureEligible[selectedType]
  const hasCurrentCerts = summary?.hasCurrentCerts ?? true
  const hrClear = summary?.hrClear ?? true
  const anyRequirementFailed = !hasCurrentCerts || !hrClear || !underCap || !projectedUnderCap || !tenureOk
  const canProceed = !anyRequirementFailed || exceptionAcknowledged

  const badCerts = useMemo(() => (summary?.certs || []).filter((c) => c.status === 'bad'), [summary?.certs])

  const loadData = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [nextSummary, categories] = await Promise.all([
        eeapService.getSummary(),
        eeapService.listPreapprovedCategories().catch(() => [...DEFAULT_EEAP_PREAPPROVED_CATEGORIES]),
      ])
      setSummary(nextSummary)
      if (categories.length) setPreapprovedCategories(categories)
    } catch (e) {
      setSummary(null)
      setLoadError(e instanceof Error ? e.message : 'Failed to load EEAP')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    setSelectedType('greenHand')
    setCategory('')
    setDescription('')
    setUrl('')
    setPurchaseMyself(false)
    setExceptionAcknowledged(false)
    setPolicyConsent(false)
    setCost('')
    setBadCertsExpanded(false)
    setError(null)
    setSignatureDate(new Date().toISOString().slice(0, 10))
    setSignatureDrawn(false)
    setTimeout(() => signaturePadRef.current?.clear(), 0)
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const hasValidSignature = () => {
    const sig = signaturePadRef.current
    return sig && !sig.isEmpty()
  }

  const equipmentDetailsOk = selectedType !== 'equipment' || !!(category || description || url)

  const handleSubmit = async () => {
    if (!hasValidSignature()) return
    if (!authRef.current?.acknowledged) {
      setError('Confirm your identity before signing.')
      return
    }
    if (needsEstimatedCost && estimatedCost == null) {
      setError('Enter the total request cost.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const dataUrl = signaturePadRef.current?.toDataURL('image/png')
      await eeapService.submitRequest({
        type: selectedType,
        ...(estimatedCost != null ? { totalRequestCost: estimatedCost } : {}),
        exceptionAcknowledged: anyRequirementFailed ? exceptionAcknowledged : false,
        purchaseMyself,
        details: selectedType === 'equipment' ? { category, description, url } : undefined,
        signature: {
          dataUrl: dataUrl || '',
          clientDate: signatureDate,
          ...(authRef.current ? { auth: authRef.current } : {}),
        },
      })
      setSubmittedConfirm(true)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit request')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen && !submittedConfirm) return null

  const dismissSubmitted = () => setSubmittedConfirm(false)

  if (submittedConfirm) {
    return (
      <div className="modal modal-open">
        <div className="modal-box max-w-md">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faCircleCheck} className="text-success text-2xl mt-0.5" />
            <div>
              <h3 className="font-bold text-lg">EEAP request submitted!</h3>
              <p className="text-sm text-base-content/70 mt-2">
                Your manager will review it. Recorded transactions appear on Profile → Advances.
              </p>
            </div>
          </div>
          <div className="modal-action">
            <button type="button" className="btn btn-primary" onClick={dismissSubmitted}>
              OK
            </button>
          </div>
        </div>
        <div className="modal-backdrop" onClick={dismissSubmitted}></div>
      </div>
    )
  }

  return (
    <>
    <div className="modal modal-open max-sm:p-0">
      <div className="modal-box h-[100dvh] max-h-[100dvh] w-full max-w-none min-h-0 rounded-none m-0 flex flex-col p-0 overflow-hidden shadow-2xl sm:m-auto sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-11/12 sm:max-w-3xl sm:rounded-box">
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-base-300 max-sm:pt-[max(0.75rem,env(safe-area-inset-top,0px))]">
          <h3 className="font-bold text-xl flex items-center gap-2 min-w-0">
            <FontAwesomeIcon icon={faShieldAlt} className="shrink-0" />
            <span className="truncate">Employee Equipment Assistance Program (EEAP)</span>
          </h3>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost shrink-0" aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 sm:px-6 py-4 space-y-4">
        {loadError && (
          <div className="alert alert-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{loadError}</span>
          </div>
        )}

        {summary?.pendingManagerReview && (
          <div className="alert alert-info">
            <span>You already have an EEAP request waiting for manager review. Recorded transactions appear on Profile → Advances.</span>
          </div>
        )}

        <div className="stats stats-vertical sm:stats-horizontal shadow bg-base-200 w-full overflow-x-hidden">
          <div className="stat min-w-0">
            <div className="stat-title">Current allowance</div>
            <div className="stat-value text-primary">${money(remaining)}</div>
            <div className="stat-desc">
              of ${money(allowance)} • resets {resetDateLabel ? `on ${resetDateLabel}` : 'on hire anniversary'}
            </div>
          </div>
          <div className="stat min-w-0">
            <div className="stat-title">Outstanding balance</div>
            <div className={`stat-value ${totalOutstanding > 0 ? 'text-warning' : ''}`}>${money(totalOutstanding)}</div>
            <div className="stat-desc">EEAP and advances combined</div>
          </div>
        </div>

        <div className="card bg-base-100">
              <div className="card-body p-4">
                <h4 className="font-semibold mb-2">Eligibility Requirements</h4>
                <ul className="space-y-2">
                  <li className="flex flex-col gap-2 p-2 rounded bg-base-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Up-to-date Certifications</div>
                        <div className="text-xs text-base-content/60">
                          {hasCurrentCerts
                            ? 'All certs current or in progress'
                            : 'One or more certs bad — update in Employee Certs tab'}
                        </div>
                      </div>
                      <span className={`badge ${loading ? 'badge-ghost' : hasCurrentCerts ? 'badge-success' : 'badge-error'}`}>
                        {loading && !summary ? 'Checking...' : hasCurrentCerts ? 'OK' : 'Bad'}
                      </span>
                    </div>
                    {!hasCurrentCerts && badCerts.length > 0 && (
                      <details className="collapse bg-base-300 rounded" open={badCertsExpanded}>
                        <summary
                          className="collapse-title min-h-0 py-2 text-sm cursor-pointer"
                          onClick={(e) => { e.preventDefault(); setBadCertsExpanded(!badCertsExpanded) }}
                        >
                          <span className="flex items-center gap-2">
                            {badCertsExpanded ? <FontAwesomeIcon icon={faChevronDown} className="text-xs" /> : <FontAwesomeIcon icon={faChevronRight} className="text-xs" />}
                            Show why
                            <span className="badge badge-error badge-sm">{badCerts.length} cert{badCerts.length !== 1 ? 's' : ''}</span>
                          </span>
                        </summary>
                        <div className="collapse-content px-0 pt-0">
                          <div className="overflow-x-auto">
                            <table className="table table-xs table-zebra">
                              <thead>
                                <tr>
                                  <th>Cert Name</th>
                                  <th>Expiration Date</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {badCerts.map((c) => (
                                  <tr key={c.type}>
                                    <td>{certDisplayLabel(c)}</td>
                                    <td>{c.expirationDate ? new Date(c.expirationDate).toLocaleDateString() : '—'}</td>
                                    <td><span className="badge badge-error badge-sm">Bad</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </details>
                    )}
                  </li>
                  <li className="flex items-center justify-between p-2 rounded bg-base-200">
                    <div>
                      <div className="font-medium">No Outstanding HR Issues/Docs</div>
                      <div className="text-xs text-base-content/60">All HR items up to date</div>
                    </div>
                    <span className={`badge ${hrClear ? 'badge-success' : 'badge-error'}`}>
                      {hrClear ? 'OK' : 'Outstanding'}
                    </span>
                  </li>
                  <li className="flex items-center justify-between p-2 rounded bg-base-200">
                    <div>
                      <div className="font-medium">Outstanding Balance Under ${money(cap)}</div>
                      <div className="text-xs text-base-content/60">
                        Current: ${money(outstandingEeap)}
                        {estimatedCost != null ? ` • After this request: ${formatUsd(projected)}` : ''}
                      </div>
                    </div>
                    <span className={`badge ${underCap && projectedUnderCap ? 'badge-success' : 'badge-error'}`}>
                      {underCap && projectedUnderCap ? 'OK' : 'Over Cap'}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="card bg-base-100">
              <div className="card-body p-4 space-y-3">
                <h4 className="font-semibold">Select Request Type</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`p-3 rounded border cursor-pointer ${selectedType === 'greenHand' ? 'border-primary ring-1 ring-primary' : 'border-base-300'} ${!tenureEligible.greenHand ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="type" className="radio radio-primary mt-1" checked={selectedType === 'greenHand'} onChange={() => setSelectedType('greenHand')} />
                      <div>
                        <div className="font-medium">Green Hand Pack</div>
                        <div className="text-xs text-base-content/60">Available immediately after hire</div>
                      </div>
                    </div>
                  </label>

                  <label className={`p-3 rounded border cursor-pointer ${selectedType === 'boots' ? 'border-primary ring-1 ring-primary' : 'border-base-300'} ${!tenureEligible.boots ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="type" className="radio radio-primary mt-1" checked={selectedType === 'boots'} onChange={() => setSelectedType('boots')} />
                      <div>
                        <div className="font-medium">Redwing Boots</div>
                        <div className="text-xs text-base-content/60">Available after 30 days</div>
                      </div>
                    </div>
                  </label>

                  <label className={`p-3 rounded border cursor-pointer ${selectedType === 'equipment' ? 'border-primary ring-1 ring-primary' : 'border-base-300'} ${!tenureEligible.equipment ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="type" className="radio radio-primary mt-1" checked={selectedType === 'equipment'} onChange={() => setSelectedType('equipment')} />
                      <div>
                        <div className="font-medium">Work Related Equipment</div>
                        <div className="text-xs text-base-content/60">Available after 60 days</div>
                      </div>
                    </div>
                  </label>
                </div>

                {selectedType === 'equipment' && (
                  <div className="mt-2 space-y-3">
                    <div>
                      <label className="label"><span className="label-text font-semibold">Pre-approved Category</span></label>
                      <select className="select select-bordered w-full" value={category} onChange={e => setCategory(e.target.value)}>
                        <option value="">Select a category</option>
                        {preapprovedCategories.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label"><span className="label-text font-semibold">Description (if not listed)</span></label>
                      <input className="input input-bordered w-full" placeholder="Describe the item you want" value={description} onChange={e => setDescription(e.target.value)} />
                    </div>
                    <div>
                      <label className="label"><span className="label-text font-semibold">Product URL</span></label>
                      <input type="url" className="input input-bordered w-full" placeholder="https://..." value={url} onChange={e => setUrl(e.target.value)} />
                      <label className="label"><span className="label-text-alt text-base-content/60">Provide a link to the exact item whenever possible.</span></label>
                    </div>
                  </div>
                )}

                {needsEstimatedCost ? (
                <div className="flex flex-col items-start gap-1 w-full">
                  <span className="font-semibold text-sm">Estimated total cost</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="input input-bordered w-full max-w-xs"
                    placeholder="0.00"
                    value={cost}
                    onChange={e => setCost(e.target.value)}
                  />
                  {estimatedCost != null && (
                    <p className="text-xs text-base-content/60">
                      Estimated employee portion after allowance: {formatUsd(employeePortion)}. Estimated new advance: {formatUsd(projected)}.
                    </p>
                  )}
                </div>
                ) : null}

                <div className="alert alert-info mt-2">
                  <FontAwesomeIcon icon={faDollarSign} />
                  <div className="text-sm space-y-1">
                    <p>Once the ${money(allowance)} LTS Allowance has been used, any remaining amount will be payroll-deducted. No more than ${money(cap)} may be outstanding at any time.</p>
                    {needsEstimatedCost ? (
                      <p>Estimated total cost is used to check the ${money(cap)} advance cap. The final amounts on the form are filled by the approver.</p>
                    ) : (
                      <p>The final amounts on the form are filled by the approver.</p>
                    )}
                  </div>
                </div>

                {anyRequirementFailed && (
                  <div className="space-y-2">
                    <div className="alert alert-warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <div>
                        <h5 className="font-semibold">One or more requirements not met</h5>
                        <p className="text-sm">You can request with an exception if approved by management.</p>
                      </div>
                    </div>
                    <label className="label cursor-pointer justify-start gap-3">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary"
                        checked={exceptionAcknowledged}
                        onChange={e => setExceptionAcknowledged(e.target.checked)}
                      />
                      <span className="label-text text-sm whitespace-normal break-words flex-1 max-w-full">Request with exception</span>
                    </label>
                  </div>
                )}

                <label className="label cursor-pointer justify-start gap-3">
                  <input type="checkbox" className="checkbox checkbox-primary" checked={purchaseMyself} onChange={e => setPurchaseMyself(e.target.checked)} />
                  <span className="label-text text-sm whitespace-normal break-words flex-1 max-w-full">
                    I would like to have my crew lead made the purchase or make this purchase myself. Leave unchecked if LTS admin should make the purchase.
                  </span>
                </label>

                <label className="label cursor-pointer justify-start items-start gap-3 mt-2 w-full">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary shrink-0 mt-1"
                    checked={policyConsent}
                    onChange={e => setPolicyConsent(e.target.checked)}
                  />
                  <span className="label-text text-sm whitespace-normal break-words flex-1 max-w-full">
                    I have read and understand the EEAP, Privacy, and Portal policies and agree to payroll deductions for any amounts beyond the ${money(allowance)} LTS allowance and for any unauthorized purchases.
                  </span>
                </label>

                <div className="border border-base-300 rounded-lg p-4 bg-base-200/50 mt-4">
                  <p className="text-sm text-base-content/90 mb-3">
                    By signing below, I certify that the information provided is accurate and complete. I understand that my electronic signature has the same legal effect as a handwritten signature and constitutes my binding agreement to the EEAP terms, including payroll deductions for any amounts beyond my LTS allowance and for unauthorized purchases.
                  </p>
                  <div className="flex flex-col gap-3">
                    <SignaturePadLock unlocked={unlocked} onUnlock={() => { void requestUnlock() }}>
                      <SignaturePad
                        ref={signaturePadRef}
                        canvasClassName="h-52 sm:h-64"
                        onEnd={() => setSignatureDrawn(true)}
                      />
                    </SignaturePadLock>
                    <div className="flex items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-base-content/60">Date</span>
                        <input
                          type="text"
                          className="input input-bordered input-sm w-32"
                          value={new Date(signatureDate).toLocaleDateString()}
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          signaturePadRef.current?.clear()
                          setSignatureDrawn(false)
                          lock()
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
        <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-base-300 bg-base-100 max-sm:pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
          {error && (
            <div className="alert alert-error mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                loading
                || !canProceed
                || !policyConsent
                || !unlocked
                || !signatureDrawn
                || (needsEstimatedCost && estimatedCost == null)
                || !equipmentDetailsOk
                || !summary
                || !!summary?.pendingManagerReview
                || summary?.formMapped === false
              }
              onClick={handleSubmit}
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
    {confirmPasswordModal}
    </>
  )
}

export default EEAPModal
